import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Button,
  Input,
  Modal,
  Spin,
  Dropdown,
  Menu,
  message,
} from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { Icon } from "@iconify/react";
import { apiGet } from "../api/request";
import apiRoutes from "../api/apiRoutes";

const { Meta } = Card;
const PAGE_LIMIT = 30;

export default function FileManager() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [search, setSearch] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");

  // ---------------- initial load + search ----------------
  useEffect(() => {
    fetchInitial();
  }, [search]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const data = await apiGet(apiRoutes.getAllAttachments, {
        page: 1,
        limit: PAGE_LIMIT,
        search,
      });

      const list = data?.items || [];

      setItems(list);
      setPage(1);
      setHasMore(list.length === PAGE_LIMIT);
    } catch (err) {
      console.error("Failed to fetch attachments:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- load more ----------------
  const loadMore = async () => {
    if (loading || !hasMore) return;

    const nextPage = page + 1;
    setLoading(true);

    try {
      const data = await apiGet(apiRoutes.getAllAttachments, {
        page: nextPage,
        limit: PAGE_LIMIT,
        search,
      });

      const list = data?.items || [];

      setItems((prev) => [...prev, ...list]);
      setPage(nextPage);
      setHasMore(list.length === PAGE_LIMIT);
    } catch (err) {
      console.error("Failed to load more attachments:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- helpers ----------------
  const getFileName = (key) => key?.split("/").pop();

  const getFileExtension = (key) => {
    const name = getFileName(key) || "";
    const parts = name.split(".");
    if (parts.length < 2) return "unknown";
    return parts.pop().toLowerCase();
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  // 🔥 copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("File URL copied");
    } catch (err) {
      console.error("Copy failed", err);
      message.error("Failed to copy");
    }
  };

  // ---------------- dynamic file type list ----------------
  const fileTypes = useMemo(() => {
    const set = new Set();
    items.forEach((item) => {
      const ext = getFileExtension(item.file_key || item.key);
      if (ext && ext !== "ds_store") set.add(ext);
    });
    return Array.from(set).sort();
  }, [items]);

  // ---------------- filtering (ONLY type filter client-side) ----------------
  const filteredItems = items
    .filter((item) => {
      if (fileTypeFilter === "all") return true;
      const key = item.file_key || item.key;
      return getFileExtension(key) === fileTypeFilter;
    })
    .filter((item) => {
      const key = item.file_key || item.key;
      return !key?.endsWith(".DS_Store");
    });

  // ---------------- filter menu ----------------
  const filterMenu = (
    <Menu
      onClick={(e) => setFileTypeFilter(e.key)}
      items={[
        { key: "all", label: "All Types" },
        ...fileTypes.map((type) => ({
          key: type,
          label: type.toUpperCase(),
        })),
      ]}
    />
  );

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>Attachments Browser</h2>

        <div style={{ display: "flex", gap: 10 }}>
          <Input
            placeholder="Search files..."
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />

          <Dropdown overlay={filterMenu} trigger={["click"]}>
            <Button icon={<FilterOutlined />}>
              {fileTypeFilter === "all"
                ? "Filter"
                : fileTypeFilter.toUpperCase()}
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 16,
        }}
      >
        {filteredItems.map((file, idx) => {
          const key = file.file_key || file.key;
          const url =
            file.url || `https://mvdebook.blr1.digitaloceanspaces.com/${key}`;

          return (
            <Card
              key={idx}
              hoverable
              cover={
                <div
                  style={{
                    height: 140,
                    background: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {isImage(url) ? (
                    <img
                      src={url}
                      alt={key}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Icon
                      icon="vscode-icons:file-type-pdf2"
                      width="80"
                      height="80"
                    />
                  )}
                </div>
              }
              actions={[
                <Button
                  type="link"
                  icon={<Icon icon="mdi:eye-outline" width="18" />}
                  onClick={() => setPreviewUrl(url)}
                >
                  Preview
                </Button>,
                <Button
                  type="link"
                  icon={<Icon icon="mdi:content-copy" width="18" />}
                  onClick={() => copyToClipboard(url)}
                >
                  Copy
                </Button>,
              ]}
            >
              <Meta
                title={getFileName(key)}
                description={
                  <span style={{ fontSize: 12 }}>{formatSize(file.size)}</span>
                }
              />
            </Card>
          );
        })}
      </div>

      {/* Load more */}
      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button onClick={loadMore} disabled={loading}>
            {loading ? <Spin size="small" /> : "Load More"}
          </Button>
        </div>
      )}

      {/* Preview Modal */}
      <Modal
        open={!!previewUrl}
        onCancel={() => setPreviewUrl(null)}
        footer={null}
        width={900}
        title="File Preview"
      >
        {previewUrl && isImage(previewUrl) ? (
          <img src={previewUrl} alt="preview" style={{ width: "100%" }} />
        ) : (
          <iframe
            src={previewUrl}
            title="preview"
            style={{ width: "100%", height: "70vh", border: "none" }}
          />
        )}
      </Modal>
    </div>
  );
}
